// src/App.js
// ============================================================
// ScholarSync AI — Router Root
// ============================================================
// All page-level components live in their own files.
// Auth-protected routes redirect to /login if no session.
// Dashboard and Admin remain accessible with API secret for demos.
// ============================================================

import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "./AuthContext";
import ProtectedRoute from "./ProtectedRoute";
import TermsOfService  from "./TermsOfService";
import PrivacyPolicy   from "./PrivacyPolicy";
import Login       from "./Login";
import Register    from "./Register";
import Dashboard   from "./Dashboard";
import ProfileForm from "./ProfileForm";
import Admin       from "./Admin";
import AdminGuard  from "./AdminGuard";
import ParentDashboard   from "./ParentDashboard";
import ParentLinkStudent from "./ParentLinkStudent";
import Checkout        from "./Checkout";
import CheckoutSuccess from "./CheckoutSuccess";
import CheckoutSuccessSocial from "./CheckoutSuccessSocial";
import CheckoutCancel  from "./CheckoutCancel";
import SocialLanding   from "./SocialLanding";
import SocialCheckoutRedirect from "./SocialCheckoutRedirect";
import Account         from "./Account";
import UpgradePlan     from "./UpgradePlan";
import FAQ             from "./FAQ";
import ForgotPassword from "./ForgotPassword";
import ResetPassword  from "./ResetPassword";
import SetPassword    from "./SetPassword";
import EmbeddedCheckoutPage from "./EmbeddedCheckoutPage";
import LandingPageV2 from './LandingPageV2';
import OurStory from './OurStory';
import Partnerships from './Partnerships';
import RedeemCode from './RedeemCode';

// Scroll to top on page navigation, or to hash target if present
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPageV2 />} />
          <Route path="/our-story" element={<OurStory />} />
          <Route path="/partnerships" element={<Partnerships />} />
          <Route path="/partnerships/redeem" element={<RedeemCode />} />
          <Route path="/social/our-story" element={<OurStory social />} />
          <Route path="/social"    element={<SocialLanding />} />
          <Route path="/individual-social" element={<SocialCheckoutRedirect planType="individual" />} />
          <Route path="/family-social"     element={<SocialCheckoutRedirect planType="family" />} />
          <Route path="/checkout-social"   element={<EmbeddedCheckoutPage />} />
          <Route path="/terms"     element={<TermsOfService />} />
          <Route path="/privacy"   element={<PrivacyPolicy />} />
          <Route path="/login"     element={<Login />} />
          <Route path="/register"  element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />
          <Route path="/set-password"    element={<SetPassword />} />

          {/* Auth-protected routes */}
          <Route path="/profile/new"
                 element={<ProtectedRoute><ProfileForm /></ProtectedRoute>} />
          <Route path="/profile/edit/:studentId"
                 element={<ProtectedRoute><ProfileForm /></ProtectedRoute>} />

          {/* Parent routes */}
          <Route path="/parent/dashboard"
                 element={<ProtectedRoute><ParentDashboard /></ProtectedRoute>} />
                    <Route path="/parent/link-student"
                 element={<ProtectedRoute><ParentLinkStudent /></ProtectedRoute>} />
          <Route path="/parent/upgrade"
                 element={<ProtectedRoute><UpgradePlan /></ProtectedRoute>} />
                    {/* Checkout routes */}
          <Route path="/checkout"
                 element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                    <Route path="/checkout/success"
                 element={<CheckoutSuccess />} />
                    <Route path="/checkout-success-social"
                 element={<CheckoutSuccessSocial />} />
          <Route path="/checkout/cancel"
                 element={<CheckoutCancel />} />
          <Route path="/faq"
                 element={<ProtectedRoute><FAQ /></ProtectedRoute>} />
          <Route path="/account"
                 element={<ProtectedRoute><Account /></ProtectedRoute>} />
          {/* Dashboard: not wrapped in ProtectedRoute so investor
              demo URLs continue to work with API secret fallback */}
          <Route path="/dashboard/:studentId" element={<Dashboard />} />

          {/* Admin: password-gated via AdminGuard; API calls send the secret the admin typed (never hardcoded) */}
          <Route path="/admin" element={<AdminGuard><Admin /></AdminGuard>} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
